import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

export type SubscriptionPlan = 'monthly' | 'yearly';

export function billingErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = String((err as { message: string }).message);
    if (message && !message.startsWith('Firebase')) {
      return message;
    }
  }
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code: string }).code);
    if (code === 'functions/unauthenticated') {
      return 'Sign in to manage your subscription.';
    }
    if (code === 'functions/failed-precondition') {
      return 'Subscriptions are not available yet. Please try again later.';
    }
    if (code === 'functions/unavailable') {
      return 'Billing service is temporarily unavailable. Try again.';
    }
  }
  return err instanceof Error ? err.message : 'Something went wrong. Please try again.';
}

/** Starts a Stripe Checkout session and returns the hosted checkout URL. */
export async function createSubscriptionCheckout(plan: SubscriptionPlan): Promise<string> {
  const fn = httpsCallable<{ plan: SubscriptionPlan }, { checkoutUrl: string }>(
    functions,
    'createSubscriptionCheckout'
  );
  const result = await fn({ plan });
  const checkoutUrl = result.data?.checkoutUrl;
  if (!checkoutUrl) {
    throw new Error('No checkout URL returned.');
  }
  return checkoutUrl;
}

/** Opens the Stripe Billing Portal and returns its URL. */
export async function createBillingPortalSession(): Promise<string> {
  const fn = httpsCallable<void, { url: string }>(functions, 'createBillingPortalSession');
  const result = await fn();
  const url = result.data?.url;
  if (!url) {
    throw new Error('No billing portal URL returned.');
  }
  return url;
}
